import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/plugins/captions.css';
import Video from 'yet-another-react-lightbox/plugins/video';
import { work } from '../../data/workData';
import { setPageTitle } from '../utils';

const WorkPage = () => {
  const { slug } = useParams();
  const project = work.find((p) => p.slug === slug);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  useEffect(() => {
    if (project) setPageTitle(project.title);
    window.scrollTo(0, 0);
  }, [project]);

  if (!project) {
    return (
      <div className="max-w-site mx-auto px-5 py-24 text-center">
        <p className="text-muted-light dark:text-muted-dark mb-4">
          Couldn't find that project.
        </p>
        <Link to="/" className="text-copper-600 dark:text-copper-400 hover:underline">
          Back home
        </Link>
      </div>
    );
  }

  const slides = [
    ...project.images.map((img) => ({
      src: `/images/${img.src}`,
      description: img.caption,
    })),
    ...(project.videos || []).map((vid) => ({
      type: 'video',
      sources: [{ src: `/images/${vid.src}`, type: 'video/mp4' }],
      description: vid.caption,
    })),
  ];

  // next / previous within the collection
  const idx = work.indexOf(project);
  const prev = work[(idx - 1 + work.length) % work.length];
  const next = work[(idx + 1) % work.length];

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="max-w-site mx-auto px-5 py-12"
    >
      <Link
        to="/"
        state={{ scrollTo: 'work' }}
        className="inline-flex items-center gap-2 text-sm font-medium text-muted-light dark:text-muted-dark hover:text-copper-600 dark:hover:text-copper-400 transition-colors mb-10"
      >
        <ArrowLeft size={16} /> All work
      </Link>

      <div className="grid lg:grid-cols-3 gap-12">
        {/* Story column */}
        <div className="lg:col-span-2">
          <p className="text-sm font-medium uppercase tracking-widest text-copper-600 dark:text-copper-400 mb-2">
            {project.category}
          </p>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-ink dark:text-bone mb-8">
            {project.title}
          </h1>
          <div className="space-y-5 text-base md:text-lg leading-relaxed text-muted-light dark:text-muted-dark max-w-2xl">
            {project.story.map((paragraph, i) => (
              <p key={i}>{paragraph}</p>
            ))}
          </div>
          {project.liveUrl && (
            <a
              href={project.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-copper-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-copper-700 transition-colors"
            >
              <ExternalLink size={15} /> Visit the site
            </a>
          )}
        </div>

        {/* Meta sidebar */}
        <aside className="lg:pt-24">
          <div className="rounded-xl border border-line-light dark:border-line-dark bg-cream/60 dark:bg-charcoal p-6">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-light dark:text-muted-dark mb-4">
              Skills &amp; tools
            </h2>
            <div className="flex flex-wrap gap-2">
              {project.skills.map((skill) => (
                <span
                  key={skill}
                  className="rounded-full border border-line-light dark:border-line-dark px-3 py-1 text-sm text-ink dark:text-bone"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {/* Video */}
      {project.youtubeId && (
        <div className="mt-14">
          <div className="relative w-full overflow-hidden rounded-xl border border-line-light dark:border-line-dark" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute inset-0 h-full w-full"
              src={`https://www.youtube.com/embed/${project.youtubeId}`}
              title={project.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Image grid */}
      <div className="mt-14 grid grid-cols-2 md:grid-cols-3 gap-4">
        {project.images.map((img, i) => (
          <button
            key={img.src}
            onClick={() => setLightboxIndex(i)}
            className="group relative block overflow-hidden rounded-lg border border-line-light dark:border-line-dark"
          >
            <img
              src={`/images/${img.src}`}
              alt={img.caption || project.title}
              loading="lazy"
              className="aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            />
            {img.caption && (
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-8 text-left text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity">
                {img.caption}
              </span>
            )}
          </button>
        ))}
        {(project.videos || []).map((vid, i) => (
          <button
            key={vid.src}
            onClick={() => setLightboxIndex(project.images.length + i)}
            className="group relative block overflow-hidden rounded-lg border border-line-light dark:border-line-dark"
          >
            <video
              src={`/images/${vid.src}`}
              className="aspect-[4/3] w-full object-cover"
              muted
              playsInline
              preload="metadata"
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <svg className="h-12 w-12 text-white opacity-80 drop-shadow" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" fillRule="evenodd" />
              </svg>
            </span>
            {vid.caption && (
              <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2 pt-8 text-left text-xs text-white">
                {vid.caption}
              </span>
            )}
          </button>
        ))}
      </div>

      <Lightbox
        open={lightboxIndex >= 0}
        close={() => setLightboxIndex(-1)}
        index={lightboxIndex}
        slides={slides}
        plugins={[Captions, Video]}
      />

      {/* Prev / next */}
      <div className="mt-16 flex justify-between border-t border-line-light dark:border-line-dark pt-8">
        <Link
          to={`/work/${prev.slug}`}
          className="text-sm font-medium text-muted-light dark:text-muted-dark hover:text-copper-600 dark:hover:text-copper-400 transition-colors"
        >
          &larr; {prev.title}
        </Link>
        <Link
          to={`/work/${next.slug}`}
          className="text-right text-sm font-medium text-muted-light dark:text-muted-dark hover:text-copper-600 dark:hover:text-copper-400 transition-colors"
        >
          {next.title} &rarr;
        </Link>
      </div>
    </motion.div>
  );
};

export default WorkPage;
